import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Upload, Clock, Timer } from 'lucide-react';
import { useBuildStacksStore, BuildStep } from '../../stores/useBuildStacksStore.js';
import BuildStackStepEditor from './BuildStackStepEditor.js';
import { runBuildStack, runStep } from '../../api/buildStacks.js';
import PlatformIcon from './PlatformIcon.js';
import BuildStackUpload from './BuildStackUpload.js';
import { formatDuration, estimateBuildDuration, calculateBuildComplexity } from '../../utils/timeUtils.js';

interface BuildStackPanelProps {
  stackId: string;
}

const BuildStackPanel: React.FC<BuildStackPanelProps> = ({ stackId }) => {
  const { stacks, update, add } = useBuildStacksStore();
  const [activeTab, setActiveTab] = useState<'Overview' | 'Steps' | 'Run' | 'Upload'>('Overview');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<BuildStep[]>([]);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [runLogs, setRunLogs] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [estimatedDuration, setEstimatedDuration] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const currentStack = stacks.find(s => s.id === stackId);

  useEffect(() => {
    if (currentStack) {
      setName(currentStack.name);
      setDescription(currentStack.description || '');
      setSteps(currentStack.steps || []);
      const complexity = calculateBuildComplexity(currentStack.steps || []);
      const estimated = estimateBuildDuration((currentStack.steps || []).length, complexity);
      setEstimatedDuration(estimated);
    } else {
      setName('');
      setDescription('');
      setSteps([]);
      setEstimatedDuration(0);
    }
    setRunLogs([]);
  }, [currentStack]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, startTime]);

  const handleSave = () => {
    const stackData = {
      name,
      description,
      steps,
      category: 'client' as const
    };

    if (currentStack) {
      update({ ...currentStack, ...stackData });
    } else {
      add(stackData);
    }
  };

  const handleRunNow = async () => {
    if (!currentStack) return;
    setIsRunning(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setRunLogs([`▶️ Running "${currentStack.name}"…`]);
    try {
      const result = await runBuildStack(currentStack.id, currentStack.steps || []);
      if (result.success) {
        setRunLogs(logs => [...logs, ...result.logs]);
        if (result.results) {
          setRunLogs(logs => [...logs, '', '📊 Execution Results:', JSON.stringify(result.results, null, 2)]);
        }
      } else {
        setRunLogs(logs => [...logs, `❌ ${result.message}`]);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setRunLogs(logs => [...logs, `❌ ${errorMessage}`]);
    } finally {
      setIsRunning(false);
      setStartTime(null);
    }
  };

  const handleRunStep = async (step: { beeName: string; command: string; inputs?: Record<string, unknown> }) => {
    setRunLogs(logs => [...logs, `▶️ Running step "${step.beeName}.${step.command}"…`]);
    try {
      const result = await runStep(step);
      if (result.success) {
        setRunLogs(logs => [...logs, ...result.logs]);
        if (result.results) {
          setRunLogs(logs => [...logs, '', '📊 Execution Results:', JSON.stringify(result.results, null, 2)]);
        }
      } else {
        setRunLogs(logs => [...logs, `❌ ${result.message}`]);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setRunLogs(logs => [...logs, `❌ ${errorMessage}`]);
    }
  };

  const handleAddStep = () => {
    setEditingStepIndex(null);
    setShowStepEditor(true);
  };



  const handleSaveStep = (step: BuildStep) => {
    if (editingStepIndex !== null) {
      const newSteps = [...steps];
      newSteps[editingStepIndex] = step;
      setSteps(newSteps);
    } else {
      setSteps([...steps, step]);
    }
    setShowStepEditor(false);
    setEditingStepIndex(null);
  };

  const handleDeleteStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSteps(items);
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-800 text-gray-100">
      {/* Tabs */}
      <div className="flex border-b border-gray-700">
        {['Overview', 'Steps', 'Run', 'Upload'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as 'Overview' | 'Steps' | 'Run' | 'Upload')}
            className={`flex items-center space-x-2 px-4 py-3 -mb-px font-medium focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              (tab === 'Steps' || tab === 'Run') && !currentStack ? 'opacity-50 pointer-events-none' : ''
            } ${
              activeTab === tab
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20'
                : 'hover:text-indigo-300 text-gray-400 hover:shadow-lg hover:shadow-blue-500/20'
            }`}
          >
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-teal-400 opacity-80 rounded-b"></div>
            )}
            {tab === 'Upload' && <Upload className="w-4 h-4" />}
            <span>{tab}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 flex-1 overflow-auto">
        {activeTab === 'Overview' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter BuildStack name…"
                className="mt-1 w-full p-2 bg-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-100"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description…"
                className="mt-1 w-full p-2 bg-gray-700 rounded h-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-100"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-600 hover:to-transparent hover:brightness-110 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-100"
                >
                  Save
                </button>
                {currentStack && (
                  <button
                    onClick={handleRunNow}
                    disabled={isRunning}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRunning ? 'Running...' : 'Run Now'}
                  </button>
                )}
              </div>
              {estimatedDuration > 0 && (
                <div className="flex items-center space-x-1 text-gray-400 text-sm">
                  <Timer className="w-4 h-4" />
                  <span>~{formatDuration(estimatedDuration)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'Steps' && (
          <div className="space-y-4">
            {steps.length === 0 ? (
              <p className="text-gray-500">No steps defined. Click "Add Step" to get started.</p>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="buildstack-steps">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-gray-700/20 rounded-lg p-2' : ''}`}
                    >
                      {steps.map((step, index) => (
                        <Draggable key={`step-${index}`} draggableId={`step-${index}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between bg-gray-700 p-3 rounded transition-all ${
                                snapshot.isDragging ? 'shadow-lg bg-gray-600 rotate-1' : 'hover:bg-gray-600'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="text-gray-400 hover:text-gray-200 cursor-grab active:cursor-grabbing"
                                >
                                  ⋮⋮
                                </div>
                                <span className="text-sm text-gray-300">#{index + 1}</span>
                                <span>{step.beeName}.{step.command}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setEditingStepIndex(index);
                                    setShowStepEditor(true);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteStep(index)}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <button
              onClick={handleAddStep}
              className="mt-2 px-3 py-1 bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-600 hover:to-transparent rounded"
            >
              + Add Step
            </button>
          </div>
        )}

        {activeTab === 'Run' && (
          <div className="space-y-4">
            {/* arcDeploy Step Controls */}
            {currentStack?.steps?.map((step, index) =>
              step.beeName === 'arcDeploy' ? (
                <div key={`arcdeploy-${index}`} className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-gray-300">Platform:</span>
                    <PlatformIcon platform={step.inputs?.platform as string} />
                    <span className="text-sm text-gray-400">{step.inputs?.platform as string}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleRunStep(step)}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm"
                    >
                      Run Deploy
                    </button>
                    <button 
                      onClick={() => handleRunStep({...step, command: 'healthCheck'})}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                    >
                      Check Health
                    </button>
                    <button 
                      onClick={() => handleRunStep({...step, command: 'rollback'})}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm"
                    >
                      Rollback
                    </button>
                  </div>
                </div>
              ) : null
            )}

            {runLogs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">No execution logs yet</p>
                <p className="text-gray-600 text-sm">Run a BuildStack to see real-time execution logs here</p>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="ml-2 text-gray-400 text-sm font-mono">BuildStack Execution Terminal</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    {(isRunning || elapsedTime > 0) && (
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Clock className="w-4 h-4" />
                        <span>
                          {isRunning ? `${formatDuration(elapsedTime)} / ~${formatDuration(estimatedDuration)}` : formatDuration(elapsedTime)}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => setRunLogs([])}
                      className="text-gray-400 hover:text-gray-200 text-sm"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-96 overflow-auto font-mono text-sm">
                  {runLogs.map((log, index) => (
                    <div key={index} className="mb-1 leading-relaxed">
                      {log.startsWith('▶️') ? (
                        <div className="text-blue-400 font-semibold">{log}</div>
                      ) : log.startsWith('✅') ? (
                        <div className="text-green-400">{log}</div>
                      ) : log.startsWith('❌') ? (
                        <div className="text-red-400">{log}</div>
                      ) : log.startsWith('🚀') ? (
                        <div className="text-purple-400">{log}</div>
                      ) : log.startsWith('🎉') ? (
                        <div className="text-yellow-400">{log}</div>
                      ) : log.startsWith('📊') ? (
                        <div className="text-cyan-400 font-semibold">{log}</div>
                      ) : log.trim() === '' ? (
                        <div className="h-2"></div>
                      ) : log.startsWith('{') || log.startsWith('[') ? (
                        <pre className="text-gray-300 bg-gray-800 p-2 rounded mt-1 overflow-auto text-xs">
                          {log}
                        </pre>
                      ) : (
                        <div className="text-gray-300">{log}</div>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center text-gray-500 mt-2">
                    <span className="animate-pulse">█</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Upload' && (
          <div className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-white mb-2">Upload BuildStack</h3>
              <p className="text-sm text-gray-400">
                Upload BuildStack JSON files to add them to your Client collection
              </p>
            </div>
            
            <BuildStackUpload 
              onUploadSuccess={(_buildStack) => {
                setActiveTab('Overview');
              }}
            />
          </div>
        )}
      </div>

      {/* Step Editor Modal */}
      {showStepEditor && (
        <BuildStackStepEditor
          isOpen={showStepEditor}
          onClose={() => {
            setShowStepEditor(false);
            setEditingStepIndex(null);
          }}
          onSave={handleSaveStep}
          initialStep={editingStepIndex !== null ? steps[editingStepIndex] : undefined}
        />
      )}
    </div>
  );
};

export default BuildStackPanel;
